import React, { useState } from 'react';

import classNames from 'classnames';

import TimeDayCell from './TimeDayCell';
import { formatTotal, toEntryUnit, unitLabel } from './timeUnits';

import { MONTH_ABBR, parseIsoDate } from 'Helpers/dateFormat';
import { getUserToday } from 'Helpers/dateTimeService';
import { formatFigure } from 'Helpers/formatService';

import SelectInput from 'CommonComponents/SelectInput';
import TextButton from 'CommonComponents/TextButton';

import TrashIcon from 'Assets/icons/trash.svg';

const FIXED_COLUMNS = 3;

// A totals cell: the dash for a zero total, otherwise the total with its hour equivalent.
const totalFigure = (days, options) => formatFigure(toEntryUnit(days, options), () => formatTotal(days, options), { precision: 2 });

// By the date's own weekday, so Saturday and Sunday are muted whichever day
// the organisation starts its week on.
const isWeekend = (date) => [0, 6].includes(parseIsoDate(date)?.getDay());

function TimeRecordRow({ row, days, options, highlighted, weekSubmitted, onCellSave, onNoteSave, onTypeChange, onRemove }) {
    // The row owns which day is being edited so Tab can hand the editor from
    // one day to the next without the cells knowing about each other.
    const [editingDate, setEditingDate] = useState(null);

    const isEditable = (day) => !weekSubmitted && !row.cells[day.date].locked && day.covered;

    const moveEditor = (fromDate, step) => {
        const from = days.findIndex((day) => day.date === fromDate);
        const next = (step > 0 ? days.slice(from + 1) : days.slice(0, from).reverse()).find(isEditable);

        if (next) {
            setEditingDate(next.date);
        }

        return !!next;
    };

    return (
        // data-row-key is how the page finds a row to scroll to when somebody
        // picks a project and role already on the week.
        <tr data-row-key={row.key} className={classNames({ '!tw-bg-blue-50': highlighted })}>
            <td className="tw-font-semibold">{row.event.name}</td>
            <td>
                <div className="tw-flex tw-flex-col">
                    <span>{row.role_name}</span>
                    <span className="tw-text-xs tw-text-charcoal-50">{row.category}</span>
                </div>
            </td>
            <td>
                <SelectInput
                    name={`type-${row.key}`}
                    aria-label={$t('Internal or freelance')}
                    value={row.type ?? ''}
                    disabled={weekSubmitted}
                    onChange={(event) => onTypeChange(row, event.target.value)}
                >
                    {/* A week whose locked days were recorded as one type and
                        whose open days are another has no single answer. Shown
                        rather than hidden, so changing it is still possible. */}
                    {row.type === null && (
                        <option value="" disabled>
                            {$t('Mixed')}
                        </option>
                    )}
                    <option value="internal">{$t('Internal')}</option>
                    <option value="freelance">{$t('Freelance')}</option>
                </SelectInput>
            </td>
            {days.map((day) => {
                const cell = row.cells[day.date];

                return (
                    <TimeDayCell
                        key={day.date}
                        quantity={cell.quantity}
                        note={cell.note}
                        locked={cell.locked}
                        covered={day.covered}
                        submitted={weekSubmitted}
                        weekend={isWeekend(day.date)}
                        options={options}
                        noteContext={$t('{{role}} · {{day}} {{month}}', {
                            role: row.role_name,
                            day: day.label,
                            month: $t(MONTH_ABBR[Number(day.date.slice(5, 7)) - 1]),
                        })}
                        editing={editingDate === day.date}
                        onStartEdit={() => setEditingDate(day.date)}
                        onStopEdit={() => setEditingDate((current) => (current === day.date ? null : current))}
                        onTab={(step) => moveEditor(day.date, step)}
                        onSave={(value) => onCellSave(row, day.date, value)}
                        onNoteSave={(note) => onNoteSave(row, day.date, note)}
                    />
                );
            })}

            <td className="inbud-numeric-cell tw-font-semibold">{totalFigure(row.total, options)}</td>
            <td className="tw-w-8">
                {!weekSubmitted && (
                    <div className="tw-flex tw-justify-center">
                        <TextButton variant="danger" onClick={() => onRemove(row)} aria-label={$t('Remove this row')}>
                            <TrashIcon className="tw-h-3 tw-w-3" />
                        </TextButton>
                    </div>
                )}
            </td>
        </tr>
    );
}

export default function TimeRecordGrid({
    week,
    days,
    rows,
    options,
    dailyTotals,
    weekTotal,
    containerRef,
    highlightedKey,
    // A submitted week is read-only until reopened: no cell, type or remove.
    weekSubmitted = false,
    onCellSave,
    onNoteSave,
    onTypeChange,
    onRemove,
}) {
    const columnCount = FIXED_COLUMNS + days.length + 2;
    const today = getUserToday();

    return (
        <div ref={containerRef} className="inbud-table_wrapper">
            <table className="inbud-table">
                <thead>
                    <tr>
                        <th className="tw-text-start tw-min-w-44">{$t('Project')}</th>
                        <th className="tw-text-start tw-min-w-32">{$t('Role')}</th>
                        <th className="tw-text-start tw-min-w-28">{$t('Type')}</th>
                        {days.map((day) => (
                            <th
                                key={day.date}
                                className={classNames('tw-text-end tw-min-w-16', {
                                    '!tw-bg-blue-15': isWeekend(day.date),
                                    'tw-text-charcoal-50': (day.locked || !day.covered) && day.date !== today,
                                    'tw-text-blue': day.date === today,
                                })}
                                aria-current={day.date === today ? 'date' : undefined}
                            >
                                {day.label}
                            </th>
                        ))}
                        <th className="tw-text-end tw-min-w-20">{$t('Total')}</th>
                        <th>
                            <span className="tw-sr-only">{$t('Actions')}</span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 && (
                        <tr>
                            <td colSpan={columnCount} className="tw-text-center tw-text-charcoal-75">
                                {$t('Nothing recorded for {{week}} yet. Add a row to start logging time.', { week: week.label })}
                            </td>
                        </tr>
                    )}

                    {rows.map((row) => (
                        <TimeRecordRow
                            key={row.key}
                            row={row}
                            days={days}
                            options={options}
                            highlighted={row.key === highlightedKey}
                            weekSubmitted={weekSubmitted}
                            onCellSave={onCellSave}
                            onNoteSave={onNoteSave}
                            onTypeChange={onTypeChange}
                            onRemove={onRemove}
                        />
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={FIXED_COLUMNS} className="tw-font-semibold">
                            {$t('Daily total')} ({unitLabel(options)})
                        </td>
                        {days.map((day) => (
                            <td key={day.date} className="inbud-numeric-cell tw-font-semibold">
                                {totalFigure(dailyTotals[day.date] ?? 0, options)}
                            </td>
                        ))}
                        <td className="inbud-numeric-cell tw-font-semibold">{totalFigure(weekTotal, options)}</td>
                        <td />
                    </tr>
                </tfoot>
            </table>
        </div>
    );
}
